import { uploadData } from 'aws-amplify/storage';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import type { UploadService } from '../types';
import { ensureAmplifyConfigured } from './amplifyConfig';

/**
 * The team the signed-in user belongs to, for the File Drop metrics partition.
 * Metrics are keyed by supplierId so a whole supplier's submissions roll up together;
 * the File Drop normalizer reads this from the uploaded object's metadata
 * (x-amz-meta-supplierid) since it has no token. Falls back to the username when no
 * team is set (a solo/pre-teams login = a team of one), matching the backend.
 */
async function resolveSupplierId(): Promise<string> {
    try {
        const attrs = await fetchUserAttributes();
        // Prefer the new `custom:supplierId`, then fall back to the legacy
        // `custom:teamId` — the attribute that is PHYSICALLY written on the live
        // pool (invite_member/approve_supplier write custom:teamId, since Cognito
        // can't add custom:supplierId to the existing pool). Members only carry
        // custom:teamId, so without this fallback their File Drop uploads get
        // stamped with their own username instead of the brand's supplierId and
        // never roll up into the team's metrics. Matches the backend's
        // _supplier_attr resolution order.
        const supplierId =
            attrs['custom:supplierId']?.trim() || attrs['custom:teamId']?.trim();
        if (supplierId) return supplierId;
    } catch {
        // Fall through to the username fallback.
    }
    try {
        const { username } = await getCurrentUser();
        return username ?? '';
    } catch {
        return '';
    }
}

/**
 * Real uploads to the private S3 bucket via Amplify Storage. The key is
 * `uploads/<cognito-identity-id>/<submissionId>/<filename>`, consistent with
 * the API and presigned channels (which use the supplier client_id as the
 * identity segment). The leading identity segment matches the IAM policy that
 * scopes each supplier to their own prefix; the submissionId is a per-upload id
 * the normalizer reads from the path, so the filename can be anything. Amplify
 * signs the request using the temporary credentials from the Identity Pool.
 */
export const awsUploadService: UploadService = {
    async uploadFile(file: File, onProgress?: (percent: number) => void): Promise<void> {
        ensureAmplifyConfigured();

        const submissionId = crypto.randomUUID();
        const supplierId = await resolveSupplierId();

        await uploadData({
            // identityId is injected by Amplify for the signed-in user.
            path: ({ identityId }) => `uploads/${identityId}/${submissionId}/${file.name}`,
            data: file,
            options: {
                contentType: file.type || 'application/octet-stream',
                // Stamp the team so the normalizer counts the Total under the
                // team partition (x-amz-meta-supplierid). Omitted when unresolved.
                ...(supplierId ? { metadata: { supplierid: supplierId } } : {}),
                onProgress: ({ transferredBytes, totalBytes }) => {
                    if (totalBytes) {
                        onProgress?.(Math.round((transferredBytes / totalBytes) * 100));
                    }
                },
            },
        }).result;
    },
};

export default awsUploadService;
